import { PlusCircleOutlined } from '@ant-design/icons'
import type { TerminalDwellResp } from '@components/DriverTracking/Dwell/api/schema'
import { useDwellAPI } from '@components/DriverTracking/Dwell/api/useDwellAPI'
import { getColumnDefs } from '@components/DriverTracking/Dwell/components/DwellTerminalGrid/columnDefs'
import { parseTimeStringToMinutes } from '@components/_SiteWide/Form/HourMinuteInput'
import { DownloadButton } from '@components/_SiteWide/Grid/ActionButtons/DownloadButton'
import { GraviButton, GraviGrid, Horizontal, NotificationMessage } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useMemo, useRef, useState } from 'react'

import { convertExpiryTimeToLocal } from '../DwellSiteGrid/DwellSiteGrid'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'dwell-terminal-config'

const validatePayload = (payload) => {
  const hasInvalidFields = payload.some((item) => item.terminal_id == null || item.product_group == null)

  if (hasInvalidFields) {
    NotificationMessage('Please fill out all required fields', 'Terminal and Product Group are required.', true)
    return true
  }
}

export function DwellTerminalGrid() {
  const gridAPIRef = useRef(null)
  const dirtyRef = useRef<any>(null)
  const { updateSetting } = useUserSettings()

  const [hasEdits, setHasEdits] = useState(false)

  const {
    useTerminalDwellOverview,
    useTerminalDwellUpsert,
    useTerminalDwellDelete,
    useTerminalDwellOverride,
    useDwellMetadata,
  } = useDwellAPI()

  const { data: metadata } = useDwellMetadata()
  const terminals = metadata?.terminals ?? []
  const product_groups = metadata?.product_groups ?? []

  const createHandleOverride = (params) => async (values) => {
    // Used to preload the form with Cell data to execute the override
    const expiryDate = convertExpiryTimeToLocal(values.date, params.data.override_expiry_local)
    const payload = {
      terminal_id: params.data.terminal_id,
      product_group: params.data.product_group,
      dwell_override: parseTimeStringToMinutes(values.overrideTime),
      override_expiry: expiryDate.toISOString(),
    }
    const result = await useTerminalDwellOverride.mutateAsync(payload)
    gridAPIRef?.current?.applyTransaction({ update: [result] })
  }

  const createHandleDeleteOverride = (params) => async () => {
    // Used to preload the form with Cell data to execute removing the override
    const payload = {
      terminal_id: params.data.terminal_id,
      product_group: params.data.product_group,
      dwell_override: null,
      override_expiry: null,
    }
    const result = await useTerminalDwellOverride.mutateAsync(payload)
    gridAPIRef?.current?.applyTransaction({ update: [result] })
  }

  const createHandleDelete = (params) => async () => {
    // Used to preload the form with Cell data to delete the row
    const payload = {
      terminal_id: params.data.terminal_id,
      product_group: params.data.product_group,
    }
    const rowId = `${params.data.terminal_id}-${params.data.product_group}`
    await useTerminalDwellDelete.mutateAsync(payload)
    gridAPIRef?.current?.applyTransaction({ remove: [{ id: rowId }] })
  }

  const columnDefs = getColumnDefs(
    terminals?.map(({ terminal }) => terminal).sort(),
    product_groups,
    createHandleOverride,
    createHandleDeleteOverride,
    createHandleDelete,
    hasEdits
  )

  const terminalLookup = useMemo(() => {
    return terminals.reduce((lookup, { terminal_id, terminal }) => {
      lookup[terminal] = terminal_id
      return lookup
    }, {})
  }, [terminals])

  const {
    data,
    isPending: isLoadingOverview,
    isFetching: isFetchingOverview,
  } = useTerminalDwellOverview() as {
    data: TerminalDwellResp[] | undefined
    isPending: boolean
    isFetching: boolean
  }

  const rowData: TerminalDwellResp[] = useMemo(() => {
    return structuredClone(data || [])
  }, [data])

  const handleAddProductGroup = () => {
    dirtyRef.current?.addDirtyRow({
      terminal_name: '',
      set_dwell: 30,
    })
    setHasEdits(true)
  }

  const handleSave = ({ dirtyChanges }) => {
    const payload = dirtyChanges.map((change) => ({
      terminal_id: terminalLookup[change.terminal_name],
      product_group: change.product_group,
      set_dwell: change.set_dwell,
      payroll_override: change.payroll_override,
    }))
    if (validatePayload(payload)) return false
    useTerminalDwellUpsert.mutate(payload)
    dirtyRef?.current?.setDirtyChanges([])
    setHasEdits(false)
    return true
  }

  const handleDiscard = () => {
    setHasEdits(false)
    const filteredData = gridAPIRef.current
      .getRenderedNodes()
      .map((node) => node.data)
      .filter((row) => !row.$inserted)
    gridAPIRef.current.setRowData(filteredData)
  }

  const handleCellEdit = (params) => {
    if (!params.valueChanged) return
    setHasEdits(true)
  }

  const agPropOverrides = useMemo(() => {
    return {
      getRowId: (row) => row.data?.id,
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
      onCellEditingStopped: handleCellEdit,
    }
  }, [])

  const controlBarProps = useMemo(() => {
    return {
      title: 'Terminals',
      actionButtons: (
        <Horizontal verticalCenter style={{ gap: '1em' }}>
          <DownloadButton fileName='Terminal Dwell Times' gridAPIRef={gridAPIRef} />
          <GraviButton
            icon={<PlusCircleOutlined />}
            buttonText='Product Grouping'
            onClick={handleAddProductGroup}
            color='primary'
          />
        </Horizontal>
      ),
    }
  }, [])

  return (
    <GraviGrid
      columnDefs={columnDefs}
      agPropOverrides={agPropOverrides}
      controlBarProps={controlBarProps}
      primaryKey='id'
      storageKey={STORAGE_KEY}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      loading={isLoadingOverview || isFetchingOverview}
      rowData={rowData}
      bulkDrawerTitle='Bulk Edit Dwell Times'
      externalRef={gridAPIRef}
      isDirtyEdit
      dirtyChangesRef={dirtyRef}
      onDirtyChangeSave={handleSave}
      onDirtyChangeDiscard={handleDiscard}
      hideSaveDisplay={false}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
